import Decimal from 'decimal.js';
import { isEmpty, keyBy } from 'lodash';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';
import {
  CombinedFormShape,
  CombinedPaths,
} from '@/modules/dispositiveProvisions/dispositiveProvisions.types';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { TransferMethod } from '@/types/schema';
import { formatPercentSmart } from '@/utils/formatting/percent';

import { useUnguardedDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import {
  buildCompositeAssetValues,
  getAssetOptionProseLabel,
  useEntityAssetOptions,
} from '../hooks/useEntityAssetOptions';
import { useHouseholdAssetsForCrossEntityDp } from '../hooks/useHouseholdAssetsForCrossEntityDp';
import { RecipientsBasePath } from './DispositiveProvisionsRecipient';

interface AssetDistributionCalloutProps {
  recipientsBasePath: RecipientsBasePath;
  idx: number;
}

/**
 * Info callout for asset-level DPs. Displays a summary of the selected assets
 * and transfer method:
 * - Single asset: "[Asset name] will distribute in kind to this recipient."
 * - Multiple assets: "The (N) selected assets will distribute in kind to this recipient."
 * - Adjusts copy for "liquidate" transfer method.
 *
 * The asset selection may include cross-entity assets picked via the
 * "View all assets to distribute" drawer at either death event, so the
 * lookup pool always spans the household.
 */
export function AssetDistributionCallout({
  recipientsBasePath,
  idx,
}: AssetDistributionCalloutProps) {
  // The DP context provides the currently-edited disposing target. When this
  // callout is rendered outside that context (e.g. the beneficiary
  // designations form, which has no DP provider), fall back to the URL-bound
  // entity/household contexts — mirroring AssetSelectorDropdown so the asset
  // name lookup spans the same pool the dropdown selected from.
  const dpContext = useUnguardedDispositiveProvisionsContext();
  const { entityId: entityDetailsEntityId } = useEntityDetailsContext();
  const { householdId: householdContextId } = useHouseholdDetailsContext();
  const entityId = dpContext.id ?? entityDetailsEntityId ?? '';
  const householdId = dpContext.householdId ?? householdContextId;
  const { control } = useFormContext<CombinedFormShape>();
  const { options: entityOptions } = useEntityAssetOptions(entityId);
  const { allOptions: householdOptions } = useHouseholdAssetsForCrossEntityDp({
    householdId,
    excludeEntityId: entityId,
  });
  const options = useMemo(
    () => [...entityOptions, ...householdOptions],
    [entityOptions, householdOptions]
  );

  const [
    assetStaticIds,
    assetGroupIds,
    insurancePolicyIds,
    ownershipStakeIds,
    transferMethod,
    bearsTax,
    dispositionAssetPercentage,
  ] = useWatch({
    control,
    name: [
      `${recipientsBasePath}.${idx}.assetStaticIds` as const satisfies CombinedPaths,
      `${recipientsBasePath}.${idx}.assetGroupIds` as const satisfies CombinedPaths,
      `${recipientsBasePath}.${idx}.insurancePolicyIds` as const satisfies CombinedPaths,
      `${recipientsBasePath}.${idx}.ownershipStakeIds` as const satisfies CombinedPaths,
      `${recipientsBasePath}.${idx}.transferMethod` as const satisfies CombinedPaths,
      `${recipientsBasePath}.${idx}.bearsTax` as const satisfies CombinedPaths,
      `${recipientsBasePath}.${idx}.dispositionAssetPercentage` as const satisfies CombinedPaths,
    ],
  });

  // Reconstruct composite values to look up display names
  const compositeValues = useMemo(
    () =>
      buildCompositeAssetValues({
        assetStaticIds: assetStaticIds as string[],
        assetGroupIds: assetGroupIds as string[],
        insurancePolicyIds: insurancePolicyIds as string[],
        ownershipStakeIds: ownershipStakeIds as string[],
      }),
    [assetStaticIds, assetGroupIds, insurancePolicyIds, ownershipStakeIds]
  );

  const totalCount = compositeValues.length;

  const text = useMemo(() => {
    if (totalCount === 0) return null;

    const isLiquidate = transferMethod === TransferMethod.LiquidateToCash;
    const pct = dispositionAssetPercentage as Decimal | null;
    const pctStr =
      pct && !pct.equals(100) ? `${formatPercentSmart(pct)}% of ` : '';

    let subject: string;
    if (totalCount === 1) {
      const optionsByValue = keyBy(options, 'value');
      const compositeValue = compositeValues[0]!;
      const singleOption = optionsByValue[compositeValue];
      // Use the prose label so insurance policies retain death-benefit
      // context (e.g. "Lincoln National ($1M death benefit)") that
      // `display` alone no longer carries. Lowercase the generic fallback
      // when a percentage prefix is present.
      const fallbackName = pctStr ? 'the selected asset' : 'The selected asset';
      const assetName = singleOption
        ? getAssetOptionProseLabel(singleOption)
        : fallbackName;
      subject = `${pctStr}${assetName}`;
    } else {
      subject = pctStr
        ? `${pctStr}the (${totalCount}) selected assets`
        : `The (${totalCount}) selected assets`;
    }

    const isInsuranceOnly =
      !isEmpty(insurancePolicyIds) &&
      isEmpty(assetStaticIds) &&
      isEmpty(assetGroupIds) &&
      isEmpty(ownershipStakeIds);

    let action: string;
    if (isInsuranceOnly) {
      // Insurance-only provisions always transfer in kind, so omit the suffix.
      // Check this first to guard against a stale LiquidateToCash transfer method
      // left over from a previous non-insurance asset selection.
      action = 'will distribute to this recipient';
    } else if (isLiquidate) {
      action = 'will distribute to this recipient, liquidated';
    } else {
      action = 'will distribute to this recipient, in kind';
    }

    const taxSuffix = bearsTax ? ', less any taxes' : '';

    return `${subject} ${action}${taxSuffix}.`;
  }, [
    totalCount,
    transferMethod,
    bearsTax,
    compositeValues,
    options,
    dispositionAssetPercentage,
    insurancePolicyIds,
    assetStaticIds,
    assetGroupIds,
    ownershipStakeIds,
  ]);

  if (!text) return null;

  return (
    <FormLayoutRow>
      <FormLayoutItem width={12}>
        <Callout severity="info-high">{text}</Callout>
      </FormLayoutItem>
    </FormLayoutRow>
  );
}
